import { getFocalPointStyles, parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import { useEagerLogoLoading } from "@insite/client-framework/Common/EagerLoadingLogo";
import useRecursiveTimeout from "@insite/client-framework/Common/Hooks/useRecursiveTimeout";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { responsiveStyleRules } from "@insite/client-framework/Common/Utilities/responsive";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconPresentationProps } from "@insite/mobius/Icon";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import Img, { ImgProps } from "@insite/mobius/Img";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Button, { ButtonIcon, ButtonPresentationProps } from "@MasterBluePrint/MobiusOverrides/Button/Button";
import productPromotionTracking from "@MasterBluePrint/Tracking/PromotionImpressions/productPromotionTracking";
import useEmblaCarousel from "embla-carousel-react";
import parse from "html-react-parser";
import React, { FC, useCallback, useEffect, useState } from "react";
import { css } from "styled-components";

interface SlideModel {
    fields: {
        slideTitle: string;
        background: "image" | "color";
        image: string;
        imageOverlay: string;
        partialOverlay: boolean;
        partialOverlayPositioning: "top" | "middle" | "bottom";
        responsiveImageBehavior: "cover" | "center" | "prioritizeHeight" | "prioritizeWidth";
        backgroundColor: string;
        backgroundColorImageContainer: string;
        svgHTML: string;
        imageLogo: string;
        heading: string;
        subheading: string;
        boxcontentheading: string;
        boxcontentsubheading: string;
        buttonLabel: string;
        buttonLink: LinkFieldValue;
        buttonVariant: "primary" | "secondary" | "tertiary";
        focalPoint:
        | "topLeft"
        | "topCenter"
        | "topRight"
        | "centerLeft"
        | "center"
        | "centerRight"
        | "bottomLeft"
        | "bottomCenter"
        | "bottomRight";
        contentPadding: number;
        centerTextVertically: boolean;
    };
}

const enum fields {
    height = "height",
    textAlignment = "textAlignment",
    showArrows = "showArrows",
    slideIndicator = "slideIndicator",
    autoplay = "autoplay",
    slides = "slides",
    responsiveFontSizes = "responsiveFontSizes",
    customFontSizes = "customFontSizes",
    h1FontSize = "h1FontSize",
    h2FontSize = "h2FontSize",
    h3FontSize = "h3FontSize",
    h4FontSize = "h4FontSize",
    h5FontSize = "h5FontSize",
    h6FontSize = "h6FontSize",
    normalFontSize = "normalFontSize",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.height]: "1/4 viewport" | "1/2 viewport" | "3/4 viewport" | "fullViewport";
        [fields.textAlignment]: "left" | "center" | "right";
        [fields.showArrows]: boolean;
        [fields.slideIndicator]: boolean;
        [fields.autoplay]: number;
        [fields.responsiveFontSizes]: boolean;
        [fields.customFontSizes]: boolean;
        [fields.h1FontSize]: number;
        [fields.h2FontSize]: number;
        [fields.h3FontSize]: number;
        [fields.h4FontSize]: number;
        [fields.h5FontSize]: number;
        [fields.normalFontSize]: number;
        [fields.slides]: SlideModel[];
    };
    extendedStyles?: SlideshowStyles;
}

export interface SlideshowStyles {
    slideshowWrapper?: InjectableCss;
    prevArrowWrapper?: InjectableCss;
    prevArrowButton?: ButtonPresentationProps;
    iconProps?: IconPresentationProps;
    carouselContainer?: InjectableCss;
    slideContainerWrapper?: InjectableCss;
    slideContentWrapper?: InjectableCss;
    slideOverlayWrapper?: InjectableCss;
    slideCenteringWrapperStyles?: InjectableCss;
    imageLogo?: TypographyPresentationProps;
    headingText?: TypographyPresentationProps;
    subheadingText?: TypographyPresentationProps;
    slideButton?: ButtonPresentationProps;
    dotsContainerWrapper?: InjectableCss;
    dotButton?: ButtonPresentationProps;
    nextArrowWrapper?: InjectableCss;
    nextArrowButton?: ButtonPresentationProps;
    menutriangle?: InjectableCss;
    headingTextcustom?: InjectableCss;
    subheadingTextcustom?: InjectableCss;
    headingTextboxcustom?: InjectableCss;
    boxcontentsubheadingcustom?: InjectableCss;
    dotsContainerWrapperNew?: InjectableCss;
}

export const slideshowStyles: SlideshowStyles = {
    slideshowWrapper: {
        css: css`
            margin-top: 0;
            padding: 0;
            position: relative;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            display: none;
                        `,
                    ],
                    "minMax",
                )}
        `,
    },
    prevArrowWrapper: {
        css: css`
            position: absolute;
            z-index: 1;
            top: 50%;
            margin-top: -1.25rem;
        `,
    },
    prevArrowButton: {
        css: css`
            display: none;
            background: none;
            border: 0;
            padding: 0;
            &:hover {
                background: none;
            }
            &:focus {
                outline: none;
            }
        `,
    },
    iconProps: {
        size: 35,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
            height: 510px;
            padding-top: 1px;
        `,
    },
    slideContainerWrapper: {
        css: css`
            display: flex;
            height: inherit;
        `,
    },
    slideContentWrapper: {
        css: css`
            display: flex;
            flex: 0 0 100%;
            position: relative;
            color: black;
            background-repeat: no-repeat;
            height: 100%;
            background-color: transparent;
            @media (max-width: 1100px) {
                background-size: auto 350px;
            }
            @media (max-width: 991px) {
                background-size: contain;
            }
            @media (max-width: 850px) {
                background-size: auto 360px;
            }
            @media (max-width: 767px) {
                background-size: contain;
            }
        `,
    },
    slideOverlayWrapper: {
        css: css`
            width: 47%;
            height: auto;
            right: 1rem;
            position: absolute;
            margin-top: 6rem;
            @media (max-width: 991px) {
                margin-top: 4rem;
            }
        `,
    },
    slideButton: {
        css: css`
            width: fit-content;
            span{
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            font-size: .875rem;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )};
            }
            
        `,
    },
    dotsContainerWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            position: absolute;
            width: 100%;
            height: 160px;
            bottom: 0;
            padding: 0 0 0 2px;
            background: #E4EEF9;
            border-bottom: 2px solid #E4EEF9;
        `,
    },
    dotsContainerWrapperNew: {
        css: css`
            min-width: 25%;
        `,
    },
    dotButton: {
        css: css`
            height: 100%;
            width: 99.2%;
            padding: .9rem 1rem 1.5rem;
            margin: 0;
            border: 0;
            background: white;
            cursor: pointer;
            opacity: 1;
            &:hover {
                background: white !important;
                opacity:1 !important;
            }
        `,
    },
    nextArrowWrapper: {
        css: css`
            position: absolute;
            z-index: 1;
            top: 50%;
            margin-top: -1.25rem;
            right: 0;
        `,
    },
    nextArrowButton: {
        css: css`
            display: none;
            background: none;
            border: 0;
            padding: 0;
            &:hover {
                background: none;
            }
            &:focus {
                outline: none;
            }
        `,
    },
    menutriangle: {
        css: css`
            width: 0;
            height: 0;
            border: 15px solid transparent;
            border-bottom-color: white;
            margin-left: .8rem;
            position: absolute;
            top: -1.8125rem;
        `,
    },
    headingTextcustom: {
        css: css`
            p {
                color: #002554;
                font-weight: 700;
                font-size: 1.3rem !important;
                text-align: left;
                line-height: 1.4rem;
                text-overflow:ellipsis;
                overflow:hidden;
                display: -webkit-box !important;
                -webkit-line-clamp: 3;
                -webkit-box-orient: vertical;
                white-space: normal;
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            font-size: 1.1rem !important;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )};
            }
            
        `,
    },
    subheadingTextcustom: {
        css: css`
            p {
                color: #919191;
                font-weight: 400;
                font-size: 1rem;
                text-align: left;
                font-family: 'Roboto', sans-serif;
                text-overflow:ellipsis;
                overflow:hidden;
                display: -webkit-box !important;
                -webkit-line-clamp: 3;
                -webkit-box-orient: vertical;
                white-space: normal;

                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            font-size: .9rem !important;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )};
            }
        `,
    },
    headingTextboxcustom: {
        css: css`
            color: #002554;
            font-weight: 500;
            font-size: 1rem;
            margin-bottom: .5rem;
            line-height: 1.1rem;
            min-height: 2rem;
            text-overflow:ellipsis;
            overflow:hidden;
            display: -webkit-box !important;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
            white-space: normal;
            @media (max-width: 991px) {
                font-size: .85rem; 
            }
        `,
    },
    boxcontentsubheadingcustom: {
        css: css`
        color: #7C878E;
        font-weight: 400;
        font-size: .85rem;
        line-height: 1rem;
        text-overflow:ellipsis;
        overflow:hidden;
        display: -webkit-box !important;
        -webkit-line-clamp: 4;
        -webkit-box-orient: vertical;
        white-space: normal;
        font-family: 'Lato', sans-serif; 
        `,
    },
    slideCenteringWrapperStyles: {
        css: css`
            text-align: left;
        `,
    },
};

export const Slideshow: FC<OwnProps> = ({ fields, extendedStyles }) => {
    const history = useHistory();
    const buttonLinks = useGetLinks(fields.slides, o => o.fields.buttonLink);
    const styles = useMergeStyles("slideshow", slideshowStyles, extendedStyles);

    const [emblaRef, embla] = useEmblaCarousel();
    const [selectedIndex, setSelectedIndex] = useState(0);

    useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("select", () => {
            setSelectedIndex(embla.selectedScrollSnap());
        });
    }, [embla]);

    useEffect(() => {
        productPromotionTracking(fields.slides);
    });

    const scrollNext = useCallback(() => {
        if (!embla) {
            return;
        }

        embla.scrollNext();
    }, [embla]);

    useRecursiveTimeout(scrollNext, fields.autoplay * 1000);

    useEffect(() => {
        if (!embla || !fields.slides || fields.slides.length === 0) {
            return;
        }

        embla.reInit({
            align: "start",
            loop: true,
        });
    }, [embla, fields.slides]);

    const onClick = (link: string | undefined) => {
        if (link) {
            history.push(link);
        }
    };

    if (!fields.slides || fields.slides.length === 0) {
        return null;
    }

    let heightStyles: string;
    switch (fields.height) {
        case "1/4 viewport":
            heightStyles = "min-height: 25vh;";
            break;
        case "1/2 viewport":
            heightStyles = "min-height: 50vh;";
            break;
        case "3/4 viewport":
            heightStyles = "min-height: 75vh;";
            break;
        case "fullViewport":
            heightStyles = "min-height: 100vh;";
            break;
    }

    const textAlignStyles = `text-align: ${fields.textAlignment};`;

    return (
        <StyledWrapper {...styles.slideshowWrapper}>
            {fields.showArrows && fields.slides.length > 1 && (
                <StyledWrapper {...styles.prevArrowWrapper}>
                    <Button {...styles.prevArrowButton} onClick={() => embla && embla.scrollPrev()}>
                        <ButtonIcon {...styles.iconProps} src={ChevronLeft} />
                    </Button>
                </StyledWrapper>
            )}
            <StyledWrapper {...styles.carouselContainer} ref={emblaRef}>
                <StyledWrapper {...styles.slideContainerWrapper}>
                    {fields.slides.map((slide, index) => {
                        const buttonLink = buttonLinks[index];

                        let responsiveImageBehaviorStyles;
                        if (slide.fields.responsiveImageBehavior === "cover") {
                            responsiveImageBehaviorStyles = "background-size: cover;";
                        } else if (slide.fields.responsiveImageBehavior === "center") {
                            responsiveImageBehaviorStyles = "background-size: contain;";
                        } else if (slide.fields.responsiveImageBehavior === "prioritizeHeight") {
                            responsiveImageBehaviorStyles = "background-size: auto 100%;";
                        } else if (slide.fields.responsiveImageBehavior === "prioritizeWidth") {
                            responsiveImageBehaviorStyles = "background-size: 100% auto;";
                        }

                        const backgroundStyles =
                            slide.fields.background === "image"
                                ? `background-image: url(${slide.fields.image});`
                                : `background-color: ${slide.fields.backgroundColor};`;
                        const focalPointStyles = getFocalPointStyles(slide.fields.focalPoint);


                        const backgroundStylesNew =
                            `background-color: ${slide.fields.backgroundColorImageContainer};`


                        let overlayPositioningStyles;
                        if (!slide.fields.partialOverlay) {
                            overlayPositioningStyles = "align-items: stretch;";
                        } else if (slide.fields.partialOverlayPositioning === "top") {
                            overlayPositioningStyles = "align-items: flex-start;";
                        } else if (slide.fields.partialOverlayPositioning === "middle") {
                            overlayPositioningStyles = "align-items: center;";
                        } else if (slide.fields.partialOverlayPositioning === "bottom") {
                            overlayPositioningStyles = "align-items: flex-end;";
                        }

                        let fontSizeStyles;
                        if (fields.responsiveFontSizes || fields.customFontSizes) {
                            fontSizeStyles = responsiveStyleRules(
                                fields.responsiveFontSizes,
                                fields.customFontSizes ? fields : undefined,
                            );
                        }

                        const slideWrapperStyles = {
                            css: css`
                                ${styles.slideContentWrapper?.css || ""}
                                ${heightStyles}
                                ${textAlignStyles}
                                ${backgroundStylesNew}
                                ${backgroundStyles}
                                ${responsiveImageBehaviorStyles}
                                ${focalPointStyles}
                                ${overlayPositioningStyles}
                                ${fontSizeStyles};
                            `,
                        };
                        const slideOverlayWrapperStyles = {
                            css: css`
                                ${styles.slideOverlayWrapper?.css || ""}
                                background-color: ${slide.fields.background === "image" ? slide.fields.imageOverlay : ""
                                };
                                padding: ${slide.fields.contentPadding}px;
                                ${slide.fields.centerTextVertically &&
                                `
                                    display: flex;
                                    align-items: left;
                                    justify-content: left;`
                                }
                            `,
                        };
                        const rawSlideTitle = slide.fields.slideTitle;
                        const uniqueSlideTitle = "homepageCarousel" + rawSlideTitle[rawSlideTitle.length - 1];

                        return (
                            // eslint-disable-next-line react/no-array-index-key
                            <StyledWrapper key={index} {...slideWrapperStyles} id={uniqueSlideTitle}>
                                <StyledWrapper {...slideOverlayWrapperStyles}>
                                    <StyledWrapper {...styles.slideCenteringWrapperStyles}>
                                        {slide.fields.svgHTML && (
                                            <StyledWrapper>
                                                {parse(slide.fields.svgHTML, {
                                                    replace: (domNode) =>
                                                        typeof domNode === 'object' &&
                                                            domNode !== null &&
                                                            'name' in domNode &&
                                                            domNode.name === 'script'
                                                            ? false
                                                            : undefined
                                                })}
                                            </StyledWrapper>
                                        )}
                                        {slide.fields.heading && (
                                            <Typography as="h3" {...styles.headingText} {...styles.headingTextcustom}>
                                                {parse(slide.fields.heading, parserOptions)}
                                            </Typography>
                                        )}
                                        {slide.fields.subheading && (
                                            <Typography {...styles.subheadingText} {...styles.subheadingTextcustom}>
                                                {parse(slide.fields.subheading, parserOptions)}
                                            </Typography>
                                        )}
                                        {(slide.fields.buttonLabel || slide.fields.buttonLink.value) && (
                                            <Button
                                                {...styles.slideButton}
                                                variant={slide.fields.buttonVariant}
                                                onClick={() => onClick(buttonLink?.url)}
                                            >
                                                {slide.fields.buttonLabel || buttonLink?.title || buttonLink?.url}
                                            </Button>
                                        )}
                                    </StyledWrapper>
                                </StyledWrapper>
                            </StyledWrapper>
                        );
                    })}
                </StyledWrapper>
            </StyledWrapper>
            {fields.slideIndicator && (
                <StyledWrapper {...styles.dotsContainerWrapper}>
                    {fields.slides.map((slide, i) => {
                        const dotColorStyles =
                            i === selectedIndex
                                ? `
                                    background: white;
                                    opacity: 1 !important;
                                    &:hover { background: white; }
                                `
                                : "";
                        const dotButtonStyles = {
                            css: css`
                                ${styles.dotButton?.css || ""}
                                ${dotColorStyles}
                            `,
                        };
                        return (
                            <StyledWrapper key={i} {...styles.dotsContainerWrapperNew}>
                                {i === selectedIndex && <StyledWrapper {...styles.menutriangle}></StyledWrapper>}
                                <StyledWrapper role="link" {...dotButtonStyles} onClick={() => embla && embla.scrollTo(i)}>
                                    <StyledWrapper style={{ display: "flex", flexDirection: "column" }}>
                                        <Typography {...styles.headingText} {...styles.headingTextboxcustom}>
                                            {" "}
                                            {slide.fields.boxcontentheading}
                                        </Typography>
                                        <Typography {...styles.headingText} {...styles.boxcontentsubheadingcustom}>
                                            {slide.fields.boxcontentsubheading}
                                        </Typography>
                                    </StyledWrapper>
                                </StyledWrapper>
                            </StyledWrapper>
                        );
                    })}
                </StyledWrapper>
            )}
            {fields.showArrows && fields.slides.length > 1 && (
                <StyledWrapper {...styles.nextArrowWrapper}>
                    <Button {...styles.nextArrowButton} onClick={() => embla && embla.scrollNext()}>
                        <ButtonIcon {...styles.iconProps} src={ChevronRight} />
                    </Button>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const contentTab = {
    displayName: "Content",
    sortOrder: 0,
};

const settingsTab = {
    displayName: "Settings",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: Slideshow,
    definition: {
        group: "Basic",
        icon: "Carousel",
        fieldDefinitions: [
            {
                name: fields.height,
                editorTemplate: "DropDownField",
                fieldType: "General",
                options: [
                    { displayName: "1/4 Viewport", value: "1/4 viewport" },
                    { displayName: "1/2 Viewport", value: "1/2 viewport" },
                    { displayName: "3/4 Viewport", value: "3/4 viewport" },
                    { displayName: "Full Viewport", value: "fullViewport" },
                ],
                defaultValue: "1/4 viewport",
                hideEmptyOption: true,
                tab: settingsTab,
            },
            {
                name: fields.textAlignment,
                editorTemplate: "DropDownField",
                defaultValue: "center",
                fieldType: "General",
                options: [
                    { displayName: "Left", value: "left" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "right" },
                ],
                hideEmptyOption: true,
                tab: settingsTab,
            },
            {
                name: fields.showArrows,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.slideIndicator,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.autoplay,
                editorTemplate: "IntegerField",
                defaultValue: 10,
                fieldType: "General",
                tab: settingsTab,
                min: 1,
            },
            {
                name: fields.responsiveFontSizes,
                editorTemplate: "CheckboxField",
                fieldType: "General",
                tab: settingsTab,
                defaultValue: true,
            },
            {
                name: fields.customFontSizes,
                editorTemplate: "CheckboxField",
                fieldType: "General",
                tab: settingsTab,
                defaultValue: false,
            },
            {
                name: fields.normalFontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h1FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: 40,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h2FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: 32,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h3FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h4FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h5FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h6FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.slides,
                editorTemplate: "ListField",
                tab: contentTab,
                getDisplay: (item: HasFields) => {
                    return item.fields.slideTitle;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "slideTitle",
                        editorTemplate: "TextField",
                        defaultValue: "Slide",
                    },
                    {
                        name: "background",
                        displayName: "Background",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Image", value: "image" },
                            { displayName: "Color", value: "color" },
                        ],
                        defaultValue: "image",
                        hideEmptyOption: true,
                    },
                    {
                        name: "backgroundColorImageContainer",
                        editorTemplate: "ColorPickerField",
                        displayName: "Image Background Color",
                        defaultValue: "",
                        isVisible: widget => widget.fields.background === "image",
                    },
                    {
                        name: "image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.background === "image",
                    },
                    {
                        name: "imageOverlay",
                        displayName: "Image Color Overlay",
                        editorTemplate: "ColorPickerField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.background === "image",
                    },
                    {
                        name: "partialOverlay",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: "partialOverlayPositioning",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Top", value: "top" },
                            { displayName: "Middle", value: "middle" },
                            { displayName: "Bottom", value: "bottom" },
                        ],
                        hideEmptyOption: true,
                        defaultValue: "bottom",
                        isVisible: widget => widget.fields.partialOverlay,
                    },
                    {
                        name: "responsiveImageBehavior",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Cover", value: "cover" },
                            { displayName: "Center", value: "center" },
                            { displayName: "Prioritize height", value: "prioritizeHeight" },
                            { displayName: "Prioritize width", value: "prioritizeWidth" },
                        ],
                        hideEmptyOption: true,
                        defaultValue: "cover",
                    },
                    {
                        name: "backgroundColor",
                        editorTemplate: "ColorPickerField",
                        displayName: "Color",
                        defaultValue: "black",
                        isVisible: widget => widget.fields.background === "color",
                    },
                    {
                        name: "focalPoint",
                        displayName: "Focal Point",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Top Left", value: "topLeft" },
                            { displayName: "Top Center", value: "topCenter" },
                            { displayName: "Top Right", value: "topRight" },
                            { displayName: "Center Left", value: "centerLeft" },
                            { displayName: "Center", value: "center" },
                            { displayName: "Center Right", value: "centerRight" },
                            { displayName: "Bottom Left", value: "bottomLeft" },
                            { displayName: "Bottom Center", value: "bottomCenter" },
                            { displayName: "Bottom Right", value: "bottomRight" },
                        ],
                        defaultValue: "center",
                        hideEmptyOption: true,
                    },
                    {
                        name: "SVG code",
                        displayName: "Svg HTML code",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: false,
                    },
                    {
                        name: "heading",
                        editorTemplate: "RichTextField",
                        displayName: "Heading",
                        defaultValue: "",
                        extendedConfig: { height: 100 },
                        expandedToolbarButtons: {
                            moreMisc: {},
                            code: {},
                        },
                    },
                    {
                        name: "subheading",
                        editorTemplate: "RichTextField",
                        displayName: "Subheading",
                        defaultValue: "",
                        extendedConfig: { height: 170 },
                        expandedToolbarButtons: {
                            moreMisc: {},
                            code: {},
                        },
                    },
                    {
                        name: "boxcontentheading",
                        editorTemplate: "TextField",
                        displayName: "BoxContentHeading",
                        defaultValue: "",
                    },
                    {
                        name: "boxcontentsubheading",
                        editorTemplate: "TextField",
                        displayName: "BoxContentSubheading",
                        defaultValue: "",
                    },
                    {
                        name: "buttonLabel",
                        editorTemplate: "TextField",
                        displayName: "Button Label",
                        defaultValue: "",
                    },
                    {
                        name: "buttonLink",
                        displayName: "Button Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                    },
                    {
                        name: "buttonVariant",
                        displayName: "Button Variant",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Primary", value: "primary" },
                            { displayName: "Secondary", value: "secondary" },
                            { displayName: "Tertiary", value: "tertiary" },
                        ],
                        hideEmptyOption: true,
                        defaultValue: "primary",
                    },
                    {
                        name: "contentPadding",
                        editorTemplate: "IntegerField",
                        defaultValue: 50,
                    },
                    {
                        name: "centerTextVertically",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;